import React from 'react';
import { IconButton, Stack, Text, useTheme } from '@fluentui/react';
import { IChangeValue, describeChangeValue, diffConfigBlob, isConfigBlobField } from './templateChangeDiff';

// One row of "what this template would change", drawn so the value is recognizable at a glance: a swatch for
// a color, a sample in the actual typeface for a font, a thumbnail for an image.
//
// The *Config fields are whole JSON documents, so a row for one of those expands into the leaf properties
// that actually differ, named as the editor names them (`card.borderRadius`, `navPodBand.backgroundColor`).
// A template can change dozens at once, so the list is capped and the remainder counted — the point is to
// recognize the change, not to audit it.

const MAX_BLOB_ROWS = 8;

const ChangeValue: React.FC<{ value: IChangeValue }> = ({ value }) => {
    const theme = useTheme();

    if (value.kind === 'color') {
        return (
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }}>
                <span
                    aria-hidden
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        background: value.raw,
                        // An outline, or white-on-white and transparent are invisible swatches.
                        border: `1px solid ${theme.palette.neutralTertiary}`,
                        flexShrink: 0,
                    }}
                />
                <Text variant='small'>{value.text}</Text>
            </Stack>
        );
    }

    if (value.kind === 'token') {
        // Named, not drawn: the value it resolves to comes from the palette this apply is about to install, so
        // there is no colour to show yet. Saying which slot it takes is the honest version.
        return (
            <Text variant='small' styles={{ root: { fontStyle: 'italic' } }}>{value.text}</Text>
        );
    }

    if (value.kind === 'font') {
        // Shown in the typeface itself. It only renders truly when the font is loaded for this page, which is
        // why the name is shown too rather than relying on the sample alone.
        return <Text variant='small' styles={{ root: { fontFamily: value.raw } }}>{value.text}</Text>;
    }

    if (value.kind === 'image') {
        return (
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }}>
                <img
                    src={value.raw}
                    alt=''
                    style={{
                        height: 16,
                        maxWidth: 48,
                        objectFit: 'contain',
                        // A transparent logo on a dark row would vanish.
                        background: theme.palette.white,
                        border: `1px solid ${theme.palette.neutralQuaternary}`,
                        flexShrink: 0,
                    }}
                />
                <Text variant='small'>{value.text}</Text>
            </Stack>
        );
    }

    return (
        <Text
            variant='small'
            styles={{ root: value.kind === 'absent' ? { color: theme.palette.neutralSecondary } : undefined }}
        >
            {value.text}
        </Text>
    );
};

const Arrow: React.FC = () => {
    const theme = useTheme();
    return <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>→</Text>;
};

/**
 * How a caller offers to leave one change out. `onToggle` is absent when exclusions are not on offer, which is
 * how the same row renders in a read-only context.
 */
export interface IChangeExclusion {
    excluded: boolean;
    onToggle: () => void;
}

// A struck-out row stays visible rather than disappearing: it is a decision the customer made and may want to
// undo, and a row that vanishes takes its own undo control with it.
const ExcludeButton: React.FC<{ exclusion: IChangeExclusion; what: string }> = ({ exclusion, what }) => (
    <IconButton
        iconProps={{ iconName: exclusion.excluded ? 'Undo' : 'Cancel' }}
        title={exclusion.excluded ? `Apply ${what} after all` : `Leave ${what} as it is`}
        ariaLabel={exclusion.excluded ? `Apply ${what} after all` : `Leave ${what} as it is`}
        onClick={exclusion.onToggle}
        styles={{ root: { height: 20, width: 20 }, icon: { fontSize: 11 } }}
    />
);

const excludedTextStyle = (excluded: boolean, color: string) =>
    excluded ? { root: { textDecoration: 'line-through', color } } : undefined;

/**
 * Just the leaf properties of a configuration document that differ. Separate from the row below so a
 * collapsible group can carry the field's name and count in its own header and render only this inside.
 */
export const TemplateBlobChanges: React.FC<{
    from?: unknown;
    to?: unknown;
    maxRows?: number;
    /** Per-property exclusion, keyed by the property's dotted path. Absent = not on offer. */
    exclusionFor?: (path: string) => IChangeExclusion;
}> =
    ({ from, to, maxRows = MAX_BLOB_ROWS, exclusionFor }) => {
        const theme = useTheme();
        const changes = diffConfigBlob(from, to);
        const shown = changes.slice(0, maxRows);
        return (
            <Stack tokens={{ childrenGap: 2 }}>
                {shown.map((change) => {
                    const exclusion = exclusionFor?.(change.path);
                    return (
                        <Stack key={change.path} horizontal verticalAlign='center' wrap tokens={{ childrenGap: 6 }}>
                            {exclusion && <ExcludeButton exclusion={exclusion} what={change.path} />}
                            <Text
                                variant='small'
                                styles={excludedTextStyle(!!exclusion?.excluded, theme.palette.neutralTertiary)
                                    ?? { root: { color: theme.palette.neutralSecondary } }}
                            >
                                {change.path}
                            </Text>
                            {exclusion?.excluded
                                ? (
                                    <Text variant='small' styles={{ root: { color: theme.palette.neutralTertiary } }}>
                                        left as it is
                                    </Text>
                                )
                                : (
                                    <>
                                        <ChangeValue value={change.from} />
                                        <Arrow />
                                        <ChangeValue value={change.to} />
                                    </>
                                )}
                        </Stack>
                    );
                })}
                {changes.length > shown.length && (
                    <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                        and {changes.length - shown.length} more
                    </Text>
                )}
            </Stack>
        );
    };

interface ITemplateChangeRowProps {
    /** The settings field name, as the plan reports it. */
    field: string;
    /** A readable form of the field name, for the row's label. */
    label: string;
    from?: unknown;
    to?: unknown;
    /** Offers to leave this whole field out. Absent = not on offer. */
    exclusion?: IChangeExclusion;
}

const TemplateChangeRow: React.FC<ITemplateChangeRowProps> = ({ field, label, from, to, exclusion }) => {
    const theme = useTheme();

    if (!isConfigBlobField(field)) {
        return (
            <Stack horizontal verticalAlign='center' wrap tokens={{ childrenGap: 6 }}>
                {exclusion && <ExcludeButton exclusion={exclusion} what={label} />}
                <Text
                    variant='small'
                    styles={excludedTextStyle(!!exclusion?.excluded, theme.palette.neutralTertiary)
                        ?? { root: { fontWeight: 600 } }}
                >
                    {label}
                </Text>
                {exclusion?.excluded
                    ? <Text variant='small' styles={{ root: { color: theme.palette.neutralTertiary } }}>left as it is</Text>
                    : (
                        <>
                            <ChangeValue value={describeChangeValue(field, from)} />
                            <Arrow />
                            <ChangeValue value={describeChangeValue(field, to)} />
                        </>
                    )}
            </Stack>
        );
    }

    const changes = diffConfigBlob(from, to);

    return (
        <Stack tokens={{ childrenGap: 2 }}>
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 6 }}>
                <Text variant='small' styles={{ root: { fontWeight: 600 } }}>{label}</Text>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {changes.length} {changes.length === 1 ? 'property' : 'properties'}
                </Text>
            </Stack>
            <Stack styles={{ root: { paddingLeft: '1em' } }}>
                <TemplateBlobChanges from={from} to={to} />
            </Stack>
        </Stack>
    );
};

export default TemplateChangeRow;
